import React, { useId } from 'react';
import { Box } from '@mui/material';

interface StratifyGlyphProps {
  /** Edge length in px. The geometry is a 40-unit square, so it scales cleanly. */
  size?: number;
  /** Drop the outer shadow — wanted inside the orbit core, where it muddies the glow. */
  flat?: boolean;
}

/**
 * The Stratify glyph: three stratified bars stacked inside a rounded square,
 * which is the whole idea of the name.
 *
 * Geometry and gradient stops are a deliberate copy of
 * `components/landing/BrandMark.tsx` so the mark is identical across the
 * marketing site and the front door of the product. It is duplicated rather
 * than imported because BrandMark hard-couples the glyph to a two-line
 * wordmark and to the landing palette, and the login screen needs the glyph
 * alone at several sizes on a dark field.
 *
 * The gradient id is per-instance: duplicate SVG gradient ids across instances
 * is a classic silent bug where every mark on the page adopts the first one's
 * fill.
 */
const StratifyGlyph: React.FC<StratifyGlyphProps> = ({ size = 40, flat = false }) => {
  const rawId = useId();
  const gradientId = `stratify-auth-glyph-${rawId.replace(/:/g, '')}`;

  return (
    <Box
      component="svg"
      viewBox="0 0 40 40"
      aria-hidden
      sx={{
        width: size,
        height: size,
        display: 'block',
        flexShrink: 0,
        filter: flat ? 'none' : 'drop-shadow(0 6px 16px rgba(91,91,240,0.45))',
      }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7b52f5" />
          <stop offset="55%" stopColor="#3b7bf5" />
          <stop offset="100%" stopColor="#2aa6f0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="40" height="40" rx="11" fill={`url(#${gradientId})`} />
      {/* Three strata, offset like layers of a stack seen in perspective. */}
      <rect x="9" y="10.5" width="22" height="4.6" rx="2.3" fill="#fff" opacity="0.95" />
      <rect x="9" y="17.7" width="15.5" height="4.6" rx="2.3" fill="#fff" opacity="0.75" />
      <rect x="9" y="24.9" width="22" height="4.6" rx="2.3" fill="#fff" opacity="0.95" />
    </Box>
  );
};

export default StratifyGlyph;
